import { faNoteSticky } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { surfaceHeaderProps } from '../SurfaceHeader';

export interface NoteFormData {
  readonly title: string;
  readonly noteType: string | null;
  readonly body: string;
}

export interface NoteComposerProps {
  readonly referenceId?: string;
  readonly title?: string;
  readonly noteTypes?: string;
  readonly body?: string;
  readonly onSave?: (data: NoteFormData) => void;
  readonly onCancel?: () => void;
}

export function NoteComposer({
  referenceId,
  title: initialTitle = '',
  noteTypes,
  body: initialBody = '',
  onSave,
  onCancel,
}: NoteComposerProps) {
  const [title, setTitle] = useState(initialTitle);
  const [noteType, setNoteType] = useState<string | null>(null);
  const [body, setBody] = useState(initialBody);

  // Sync when the agent rebinds props after mount (A2UI can update bindings live)
  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);
  useEffect(() => {
    setBody(initialBody);
  }, [initialBody]);

  const noteTypeList = noteTypes
    ? noteTypes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : ['General', 'Follow-up', 'Underwriting', 'Compliance', 'Claims'];

  return (
    <Paper
      radius="md"
      withBorder
      p="lg"
      style={{
        borderColor: 'var(--mantine-color-blue-2)',
        width: '100%',
      }}
    >
      {/* Header */}
      <Group justify="space-between" {...surfaceHeaderProps('lg')}>
        <Group gap="xs">
          <FontAwesomeIcon
            icon={faNoteSticky}
            style={{
              color: 'var(--mantine-color-blue-6)',
              width: 18,
              height: 18,
            }}
          />
          <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
            NOTE
          </Text>
          {referenceId && (
            <Text fz={13} c="dimmed">
              {referenceId}
            </Text>
          )}
        </Group>
        <Group gap="xs">
          <Button variant="outline" color="gray" size="xs" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="outline"
            color="blue"
            size="xs"
            onClick={() => onSave?.({ title, noteType, body })}
          >
            Save Note
          </Button>
        </Group>
      </Group>

      <Stack gap="sm">
        <Group gap="sm" wrap="nowrap">
          <TextInput
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            size="sm"
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Select Type"
            data={noteTypeList}
            value={noteType}
            onChange={setNoteType}
            size="sm"
            style={{ width: 160 }}
          />
        </Group>
        <Textarea
          placeholder="Type something to remember..."
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          autosize
          minRows={6}
          size="sm"
        />
      </Stack>
    </Paper>
  );
}
